import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Table } from 'dexie';
import { Download, Info, Moon, Sun, Globe, Trash2, Upload, Database, Cloud } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DebugDialog } from '@/components/features/settings/DebugDialog';
import {
  ImportPreviewCard,
  type DuplicateSummary,
  type ImportStrategy,
} from '@/components/features/settings/ImportPreviewCard';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { db, resetDatabase, DATABASE_VERSION } from '@/db';
import { clearRemoteData } from '@/db/remote-reset';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { env } from '@/lib/env';
import { handleBackgroundError } from '@/lib/error-handler';
import { fadeIn, transitions } from '@/lib/motion-config';
import { toastSuccess, toastError } from '@/lib/toast-helpers';
import { BackupPayloadSchema, type BackupPayload, SCHEMA_VERSIONS } from '@/schemas';
import { useAuthStore, usePwaInstallStore, useSessionStore, useUIStore } from '@/stores';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

type ImportPreviewState = {
  readonly payload: BackupPayload;
  readonly fileName: string;
  readonly summary: DuplicateSummary;
};

type IdentifiedRecord = { id: string };

const toIdSet = (keys: readonly unknown[]): Set<string> => {
  const result = new Set<string>();
  for (const key of keys) {
    result.add(String(key));
  }
  return result;
};

const countDuplicates = <T extends IdentifiedRecord>(
  records: readonly T[],
  idSet: Set<string>,
): number => {
  let count = 0;
  for (const record of records) {
    if (idSet.has(record.id)) {
      count += 1;
    }
  }
  return count;
};

const computeDuplicateSummary = async (
  payload: BackupPayload,
  database: typeof db,
): Promise<DuplicateSummary> => {
  const [
    existingSessionEventIds,
    existingPlayerEventIds,
    existingPlayerIds,
    existingGameDefinitionIds,
    existingTeamPresetIds,
    existingSessionMetadataIds,
    existingSessionTemplateIds,
  ] = await Promise.all([
    database.sessionEvents.toCollection().primaryKeys(),
    database.playerEvents.toCollection().primaryKeys(),
    database.players.toCollection().primaryKeys(),
    database.gameDefinitions.toCollection().primaryKeys(),
    database.teamPresets.toCollection().primaryKeys(),
    database.sessionMetadata.toCollection().primaryKeys(),
    database.sessionTemplates.toCollection().primaryKeys(),
  ]);

  const sessionEventIdSet = toIdSet(existingSessionEventIds);
  const playerEventIdSet = toIdSet(existingPlayerEventIds);
  const playerIdSet = toIdSet(existingPlayerIds);
  const gameDefinitionIdSet = toIdSet(existingGameDefinitionIds);
  const teamPresetIdSet = toIdSet(existingTeamPresetIds);
  const sessionMetadataIdSet = toIdSet(existingSessionMetadataIds);
  const sessionTemplateIdSet = toIdSet(existingSessionTemplateIds);

  return {
    sessionEvents: {
      incoming: payload.data.sessionEvents.length,
      existing: sessionEventIdSet.size,
      duplicates: countDuplicates(payload.data.sessionEvents, sessionEventIdSet),
    },
    playerEvents: {
      incoming: payload.data.playerEvents.length,
      existing: playerEventIdSet.size,
      duplicates: countDuplicates(payload.data.playerEvents, playerEventIdSet),
    },
    players: {
      incoming: payload.data.players.length,
      existing: playerIdSet.size,
      duplicates: countDuplicates(payload.data.players, playerIdSet),
    },
    gameDefinitions: {
      incoming: payload.data.gameDefinitions.length,
      existing: gameDefinitionIdSet.size,
      duplicates: countDuplicates(payload.data.gameDefinitions, gameDefinitionIdSet),
    },
    teamPresets: {
      incoming: payload.data.teamPresets.length,
      existing: teamPresetIdSet.size,
      duplicates: countDuplicates(payload.data.teamPresets, teamPresetIdSet),
    },
    sessionMetadata: {
      incoming: payload.data.sessionMetadata.length,
      existing: sessionMetadataIdSet.size,
      duplicates: countDuplicates(payload.data.sessionMetadata, sessionMetadataIdSet),
    },
    sessionTemplates: {
      incoming: payload.data.sessionTemplates.length,
      existing: sessionTemplateIdSet.size,
      duplicates: countDuplicates(payload.data.sessionTemplates, sessionTemplateIdSet),
    },
  } satisfies DuplicateSummary;
};

const addRecordsWithoutOverwriting = async <T extends IdentifiedRecord, TKey>(
  table: Table<T, TKey>,
  records: readonly T[],
): Promise<void> => {
  if (records.length === 0) {
    return;
  }

  const existingKeys = await table.toCollection().primaryKeys();
  const existingIds = toIdSet(existingKeys);

  const recordsToInsert: T[] = [];
  for (const record of records) {
    if (!existingIds.has(record.id)) {
      recordsToInsert.push(record);
    }
  }

  if (recordsToInsert.length > 0) {
    await table.bulkAdd(recordsToInsert);
  }
};

const applyBackupWithStrategy = async (
  payload: BackupPayload,
  strategy: ImportStrategy,
  database: typeof db,
): Promise<void> => {
  const { data } = payload;

  if (strategy === 'replace') {
    await resetDatabase();
  }

  await database.transaction('rw', database.tables, async () => {
    if (strategy === 'replace') {
      if (data.sessionEvents.length > 0) {
        await database.sessionEvents.bulkAdd(data.sessionEvents);
      }
      if (data.playerEvents.length > 0) {
        await database.playerEvents.bulkAdd(data.playerEvents);
      }
      if (data.players.length > 0) {
        await database.players.bulkAdd(data.players);
      }
      if (data.gameDefinitions.length > 0) {
        await database.gameDefinitions.bulkAdd(data.gameDefinitions);
      }
      if (data.teamPresets.length > 0) {
        await database.teamPresets.bulkAdd(data.teamPresets);
      }
      if (data.sessionMetadata.length > 0) {
        await database.sessionMetadata.bulkAdd(data.sessionMetadata);
      }
      if (data.sessionTemplates.length > 0) {
        await database.sessionTemplates.bulkAdd(data.sessionTemplates);
      }
      return;
    }

    await addRecordsWithoutOverwriting(database.sessionEvents, data.sessionEvents);
    await addRecordsWithoutOverwriting(database.playerEvents, data.playerEvents);
    await addRecordsWithoutOverwriting(database.players, data.players);
    await addRecordsWithoutOverwriting(database.gameDefinitions, data.gameDefinitions);
    await addRecordsWithoutOverwriting(database.teamPresets, data.teamPresets);
    await addRecordsWithoutOverwriting(database.sessionMetadata, data.sessionMetadata);
    await addRecordsWithoutOverwriting(database.sessionTemplates, data.sessionTemplates);
  });
};

function SettingsPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const clearSession = useSessionStore((state) => state.clearSession);
  const resetSessionStore = useSessionStore((state) => state.resetStore);
  const resetUIStore = useUIStore((state) => state.reset);
  const resetPwaStore = usePwaInstallStore((state) => state.reset);
  const signOut = useAuthStore((state) => state.signOut);
  const userId = useAuthStore((state) => state.userId);
  const [isClearingLocal, setIsClearingLocal] = useState(false);
  const [isClearingRemote, setIsClearingRemote] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [showClearLocalConfirm, setShowClearLocalConfirm] = useState(false);
  const [showClearRemoteConfirm, setShowClearRemoteConfirm] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [pendingImport, setPendingImport] = useState<ImportPreviewState>();
  const [importStrategy, setImportStrategy] = useState<ImportStrategy>('replace');
  const [isApplyingImport, setIsApplyingImport] = useState(false);
  const [isDebugDialogOpen, setIsDebugDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);
  const isMobile = useIsMobile();

  const appVersion = env.appVersion;

  useDocumentTitle(`${t('settings.title')} - ${t('app.name', 'One Score')}`);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current !== undefined) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const handleCancelImportPreview = () => {
    setPendingImport(undefined);
    setImportStrategy('replace');
  };

  const handleConfirmImport = async () => {
    if (!pendingImport) {
      return;
    }

    setIsApplyingImport(true);

    try {
      await applyBackupWithStrategy(pendingImport.payload, importStrategy, db);
      clearSession();
      resetSessionStore();
      setPendingImport(undefined);
      setImportStrategy('replace');
      toastSuccess(t('settings.dataManagement.importSuccess'));
      void navigate({ to: '/' });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('settings.dataManagement.importError');
      toastError(errorMessage);
    } finally {
      setIsApplyingImport(false);
    }
  };

  const handleClearLocalData = async () => {
    setIsClearingLocal(true);

    try {
      await resetDatabase();
      resetSessionStore();
      resetUIStore();
      resetPwaStore();
      setShowClearLocalConfirm(false);
      toastSuccess(t('settings.dataManagement.clearLocalSuccess'));
      void navigate({ to: '/' });
    } catch (error) {
      // Log detailed error for debugging
      handleBackgroundError(error, { operation: 'clearLocalData' });
      // Show generic error message to user
      toastError(t('settings.dataManagement.clearLocalError'));
    } finally {
      setIsClearingLocal(false);
    }
  };

  const handleClearRemoteData = async () => {
    if (!userId) {
      toastError(t('settings.dataManagement.clearRemoteError'));
      return;
    }

    setIsClearingRemote(true);

    try {
      await clearRemoteData(userId);
      setShowClearRemoteConfirm(false);
      toastSuccess(t('settings.dataManagement.clearRemoteSuccess'));
    } catch (error) {
      // Log detailed error for debugging
      handleBackgroundError(error, { operation: 'clearRemoteData', userId });
      // Show generic error message to user
      toastError(t('settings.dataManagement.clearRemoteError'));
    } finally {
      setIsClearingRemote(false);
    }
  };

  const handleClearAllData = async () => {
    setIsClearingAll(true);

    try {
      // Clear local data
      await resetDatabase();
      resetSessionStore();
      resetUIStore();
      resetPwaStore();

      // Clear remote data if authenticated
      if (userId) {
        try {
          await clearRemoteData(userId);
        } catch (error) {
          handleBackgroundError(error, { operation: 'clearRemoteDataDuringClearAll', userId });
        }
      }

      void i18n.changeLanguage('en');
      try {
        await signOut();
      } catch (error) {
        handleBackgroundError(error, { operation: 'signOutDuringClearAll' });
      }
      setShowClearAllConfirm(false);
      toastSuccess(t('settings.dataManagement.clearAllSuccess'));
      void navigate({ to: '/' });
    } catch (error) {
      // Log detailed error for debugging
      handleBackgroundError(error, { operation: 'clearAllData', userId });
      // Show generic error message to user
      toastError(t('settings.dataManagement.clearAllError'));
    } finally {
      setIsClearingAll(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);

    try {
      const [
        sessionEvents,
        playerEvents,
        players,
        gameDefinitions,
        teamPresets,
        sessionMetadata,
        sessionTemplates,
      ] = await Promise.all([
        db.sessionEvents.toArray(),
        db.playerEvents.toArray(),
        db.players.toArray(),
        db.gameDefinitions.toArray(),
        db.teamPresets.toArray(),
        db.sessionMetadata.toArray(),
        db.sessionTemplates.toArray(),
      ]);

      const exportData: BackupPayload = BackupPayloadSchema.parse({
        metadata: {
          exportedAt: new Date().toISOString(),
          appVersion,
          databaseVersion: DATABASE_VERSION,
          schemaVersions: {
            sessionEvent: SCHEMA_VERSIONS.SESSION_EVENT,
            playerEvent: SCHEMA_VERSIONS.PLAYER_EVENT,
          },
          tableCounts: {
            sessionEvents: sessionEvents.length,
            playerEvents: playerEvents.length,
            players: players.length,
            gameDefinitions: gameDefinitions.length,
            teamPresets: teamPresets.length,
            sessionMetadata: sessionMetadata.length,
            sessionTemplates: sessionTemplates.length,
          },
        },
        data: {
          sessionEvents,
          playerEvents,
          players,
          gameDefinitions,
          teamPresets,
          sessionMetadata,
          sessionTemplates,
        },
      });

      const blob = new Blob([JSON.stringify(exportData, undefined, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `one-score-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toastSuccess(t('settings.dataManagement.exportSuccess'));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('settings.dataManagement.exportError');
      toastError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);

    try {
      const text = await file.text();
      let parsedJson: unknown;

      try {
        parsedJson = JSON.parse(text);
      } catch {
        throw new Error(t('settings.dataManagement.importInvalid'));
      }

      const parsedPayload = BackupPayloadSchema.parse(parsedJson);
      const summary = await computeDuplicateSummary(parsedPayload, db);

      setPendingImport({
        payload: parsedPayload,
        fileName: file.name,
        summary,
      });
      setImportStrategy('replace');
      toastSuccess(t('settings.dataManagement.importReady'));
    } catch (error) {
      setPendingImport(undefined);
      const errorMessage =
        error instanceof Error ? error.message : t('settings.dataManagement.importError');
      toastError(errorMessage);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <motion.div
      key={routeKey}
      variants={fadeIn}
      initial={shouldAnimate ? 'initial' : false}
      animate="animate"
      transition={transitions.springGentle}
      className="w-full max-w-4xl mx-auto space-y-6 px-4 py-6 md:py-8"
    >
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground">{t('settings.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('settings.description')}</p>
      </div>

      <Separator />

      {/* App Information Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle>{t('settings.appInfo.section')}</CardTitle>
          </div>
          <CardDescription>{t('settings.appInfo.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="flex items-center justify-between py-2 select-none"
            onDoubleClick={() => {
              if (!isMobile) {
                setIsDebugDialogOpen(true);
              }
            }}
            onTouchStart={() => {
              if (isMobile) {
                longPressTimerRef.current = globalThis.setTimeout(() => {
                  setIsDebugDialogOpen(true);
                }, 800);
              }
            }}
            onTouchEnd={() => {
              if (longPressTimerRef.current !== undefined) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = undefined;
              }
            }}
            onTouchCancel={() => {
              if (longPressTimerRef.current !== undefined) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = undefined;
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsDebugDialogOpen(true);
              }
            }}
            aria-label={
              isMobile
                ? t('settings.debug.openDialogMobile', 'Long press to open debug information')
                : t('settings.debug.openDialog', 'Double-click to open debug information')
            }
          >
            <span className="text-sm font-medium text-muted-foreground">
              {t('settings.appInfo.version')}
            </span>
            <span className="text-sm font-semibold text-foreground">{appVersion}</span>
          </div>
        </CardContent>
      </Card>

      {/* Install App Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle>{t('settings.install.section')}</CardTitle>
          </div>
          <CardDescription>{t('settings.install.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <InstallPrompt variant="settings" />
        </CardContent>
      </Card>

      {/* Preferences Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle>{t('settings.preferences.section')}</CardTitle>
          </div>
          <CardDescription>{t('settings.preferences.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Moon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t('settings.preferences.theme')}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t('settings.preferences.themeDescription')}
                </p>
              </div>
            </div>
            <ThemeToggle />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Globe className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t('settings.preferences.language')}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t('settings.preferences.languageDescription')}
                </p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </CardContent>
      </Card>

      {/* Data Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle>{t('settings.dataManagement.section')}</CardTitle>
          </div>
          <CardDescription>{t('settings.dataManagement.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Backup & Restore */}
          <div className="space-y-3">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-foreground">
                {t('settings.dataManagement.backupRestore')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('settings.dataManagement.backupRestoreDescription')}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={(e) => {
                void handleImportData(e);
              }}
              disabled={isImporting}
              className="hidden"
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => {
                  void handleExportData();
                }}
                disabled={isExporting || isApplyingImport}
                className="flex-1"
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting
                  ? t('settings.dataManagement.exporting')
                  : t('settings.dataManagement.exportButton')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                disabled={isImporting}
                className="flex-1"
              >
                <Upload className="mr-2 h-4 w-4" />
                {isImporting
                  ? t('settings.dataManagement.importing')
                  : t('settings.dataManagement.importButton')}
              </Button>
            </div>
          </div>

          {pendingImport && (
            <ImportPreviewCard
              payload={pendingImport.payload}
              fileName={pendingImport.fileName}
              summary={pendingImport.summary}
              importStrategy={importStrategy}
              onStrategyChange={setImportStrategy}
              onConfirm={() => {
                void handleConfirmImport();
              }}
              onCancel={handleCancelImportPreview}
              isConfirming={isApplyingImport}
            />
          )}

          <Separator />

          {/* Clear Local Data */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-destructive">
                  {t('settings.dataManagement.clearLocalTitle')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('settings.dataManagement.clearLocalDescription')}
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              onClick={() => {
                setShowClearLocalConfirm(true);
              }}
              disabled={isClearingLocal || isClearingAll}
              className="w-full sm:w-auto"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('settings.dataManagement.clearLocalButton')}
            </Button>
          </div>

          {/* Clear Remote Data - Only show if authenticated */}
          {userId && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Cloud className="h-4 w-4 text-destructive" aria-hidden="true" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-destructive">
                      {t('settings.dataManagement.clearRemoteTitle')}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.dataManagement.clearRemoteDescription')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowClearRemoteConfirm(true);
                  }}
                  disabled={isClearingRemote || isClearingAll}
                  className="w-full sm:w-auto"
                >
                  <Cloud className="mr-2 h-4 w-4" />
                  {t('settings.dataManagement.clearRemoteButton')}
                </Button>
              </div>
            </>
          )}

          {/* Clear All Data - Only show if authenticated */}
          {userId && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-destructive">
                      {t('settings.dataManagement.clearAllTitle')}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.dataManagement.clearAllDescription')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowClearAllConfirm(true);
                  }}
                  disabled={isClearingLocal || isClearingRemote || isClearingAll}
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.dataManagement.clearAllButton')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Clear Local Data Confirmation Dialog */}
      <AlertDialog open={showClearLocalConfirm} onOpenChange={setShowClearLocalConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.dataManagement.clearLocalConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.dataManagement.clearLocalConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearingLocal}>
              {t('settings.dataManagement.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleClearLocalData();
              }}
              disabled={isClearingLocal}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearingLocal
                ? t('settings.dataManagement.confirming')
                : t('settings.dataManagement.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Remote Data Confirmation Dialog */}
      {userId && (
        <AlertDialog open={showClearRemoteConfirm} onOpenChange={setShowClearRemoteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('settings.dataManagement.clearRemoteConfirmTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('settings.dataManagement.clearRemoteConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isClearingRemote}>
                {t('settings.dataManagement.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  void handleClearRemoteData();
                }}
                disabled={isClearingRemote}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isClearingRemote
                  ? t('settings.dataManagement.confirming')
                  : t('settings.dataManagement.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Clear All Data Confirmation Dialog - Only show if authenticated */}
      {userId && (
        <AlertDialog open={showClearAllConfirm} onOpenChange={setShowClearAllConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('settings.dataManagement.clearAllConfirmTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('settings.dataManagement.clearAllConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isClearingAll}>
                {t('settings.dataManagement.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  void handleClearAllData();
                }}
                disabled={isClearingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isClearingAll
                  ? t('settings.dataManagement.confirming')
                  : t('settings.dataManagement.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Debug Dialog */}
      <DebugDialog open={isDebugDialogOpen} onOpenChange={setIsDebugDialogOpen} />
    </motion.div>
  );
}
